import * as z from 'zod/mini';
import { Prettify } from 'zod/v4/core/util';

export const eventsName = {
  martech: 'martech-tracking-framework-event',
} as const;

export type WidgetData = {
  name: string; // A label to describe the component
  instanceId: string; // "376d" — 4-character hex string that uniquely identifies the widget
  version: string; // "1.0.0" — Semantic version of the component
  ref?: HTMLElement; // HTMLElement — Reference to the widget’s root element
  bets: Bet[]; // An array describing the list of the current bets displayed on the widget.
};

export type Bet = {
  gameId: string; // "29727162" — ID of the game related to the bet
  position: number; // -1 or 1, 2, 3... — Position of the bet in the UI
  league: string;
  market: BetMarket; // The relevant bet market
  selection: BetSelection; //The relevant bet selection
};

export type BetMarket = {
  label: string; // "Spread" — Full Pick Label
  id: number; //An integer representing The CBS Sports market id.
};

export type BetSelection = {
  odds: number; // -112 — American odds format
  line?: number | null; // -3.5 — The line of the selection, if applicable
  side?: string | null; // "over" — Used for O/U markets
  label: string; // "Indiana +3.5 -160" — Describe the selection name that the user sees on the widget.
  entity?: BetSelectionEntity; //Describes the sports entity tied to the bet if applicable.
};

export type BetSelectionEntity = {
  type: 'player' | 'team'; // "team" — Type of the entity
  id: string; // 345 — ID of the team or player
};

type PickSnippetEvent = {
  eventName: (typeof eventsName)[keyof typeof eventsName]; // "martech-tracking-framework-event" — Name of the event
  widgetData: WidgetData;
};

type PickSnippetImpressionWidgetDataBetSelection = Pick<BetSelection, 'odds' | 'line' | 'entity' | 'side'>;

export type PickSnippetImpressionWidgetDataBet = Prettify<
  Pick<Bet, 'gameId' | 'position' | 'market'> & {
    selection: PickSnippetImpressionWidgetDataBetSelection;
  }
>;

export type PickSnippetImpressionWidgetData = Omit<WidgetData, 'bets'> & {
  bets: Array<PickSnippetImpressionWidgetDataBet>;
};

export type PickSnippetErrorWidgetData = Omit<WidgetData, 'bets'>;

export type PickSnippetEventImpression = Pick<PickSnippetEvent, 'eventName'> & {
  action: 'impression';
  widgetData: PickSnippetImpressionWidgetData;
};

export type PickSnippetEventViewed = PickSnippetEvent & {
  action: 'viewed';
};

export type PickSnippetEventError = Pick<PickSnippetEvent, 'eventName'> & {
  action: 'error';
  payload: { error: string };
  widgetData: PickSnippetErrorWidgetData;
};

type PickSnippetEvents = PickSnippetEventError | PickSnippetEventImpression | PickSnippetEventViewed;

// ========================= SCHEMAS START
const instanceIdSchema = z.string().check(z.minLength(4), z.maxLength(4), z.trim());

const widgetDataDefaultSchema = z.object({
  name: z.string(),
  instanceId: instanceIdSchema,
  version: z.string(),
  // ref: z.instanceof(HTMLElement),
});

const entitySchema: z.ZodMiniType<BetSelectionEntity> = z.object({
  type: z.enum(['player', 'team']),
  id: z.string(),
});

const marketSchema: z.ZodMiniType<BetMarket> = z.object({
  label: z.string(),
  id: z.number(),
});

// viewed tracking schema
const BetSchema: z.ZodMiniType<Bet> = z.object({
  gameId: z.string().check(z.minLength(1), z.trim()),
  position: z.number(),
  league: z.string(),
  market: marketSchema,
  selection: z.object({
    odds: z.number(),
    line: z.optional(z.number()),
    side: z.optional(z.string()),
    label: z.string(),
    entity: z.optional(entitySchema),
  }),
});

const schemaViewedWidgetData: z.ZodMiniType<WidgetData> = z.object({
  ...widgetDataDefaultSchema.shape,
  bets: z.array(BetSchema).check(z.minLength(1)),
});

const viewedTrackingSchema: z.ZodMiniType<Omit<PickSnippetEventViewed, 'eventName'>> = z.object({
  action: z.enum(['viewed']),
  widgetData: schemaViewedWidgetData,
});

// Impression tracking schema
const impressionBetSelectionSchema: z.ZodMiniType<PickSnippetImpressionWidgetDataBetSelection> = z.object({
  odds: z.number(),
  line: z.optional(z.number()),
  side: z.string(),
  entity: z.optional(entitySchema),
});

const impressionBetSchema: z.ZodMiniType<PickSnippetImpressionWidgetDataBet> = z.object({
  gameId: z.string().check(z.minLength(1), z.trim()),
  position: z.number(),
  market: marketSchema,
  selection: impressionBetSelectionSchema,
});

const schemaImpressionWidgetData: z.ZodMiniType<PickSnippetImpressionWidgetData> = z.object({
  ...widgetDataDefaultSchema.shape,
  bets: z.array(impressionBetSchema),
});

const impressionTrackingSchema: z.ZodMiniType<Omit<PickSnippetEventImpression, 'eventName'>> = z.object({
  action: z.enum(['impression']),
  widgetData: schemaImpressionWidgetData,
});

// Error Schema
const errorTrackingSchema: z.ZodMiniType<Omit<PickSnippetEventError, 'eventName'>> = z.object({
  action: z.enum(['error']),
  payload: z.object({
    error: z.string().check(z.minLength(1), z.trim()),
  }),
  widgetData: widgetDataDefaultSchema,
});

type EventsData = Omit<PickSnippetEvents, 'eventName'>;

function dispatchEvent(eventName, detail: EventsData) {
  const customEvent = new CustomEvent(eventName, {
    detail,
  });
  window.dispatchEvent(customEvent);
}

type Schemas = typeof errorTrackingSchema | typeof impressionTrackingSchema | typeof viewedTrackingSchema;

// ========================= SCHEMAS END

export function validateSchema(schema: Schemas, data: EventsData) {
  const result = schema.safeParse(data);
  if (!result.success) {
    console.warn('Validation failed:', result.error);
    return false;
  }
  return true;
}

export function trackEvent(event: PickSnippetEvents) {
  const { eventName, ...detail } = event;
  switch (event.action) {
    case 'error':
      if (!validateSchema(errorTrackingSchema, detail)) return;
      dispatchEvent(eventName, detail);
      break;
    case 'impression':
      if (!validateSchema(impressionTrackingSchema, detail)) return;
      dispatchEvent(eventName, detail);
      break;
    case 'viewed':
      if (!validateSchema(viewedTrackingSchema, detail)) return;
      dispatchEvent(eventName, detail);
      break;
    default: {
      console.warn(`Unknown event action: ${event}`);
      return;
    }
  }
}

export function isValidInstanceId(instanceId?: unknown): boolean {
  const validation = instanceIdSchema.safeParse(instanceId);
  if (!validation.success) {
    console.warn(`Invalid instanceId: ${instanceId}`, validation.error);
  }
  return validation.success;
}

export function setInstanceIdAttribute(el: HTMLElement, instanceId: string): string {
  const existing = el.getAttribute('data-instance-id');

  if (isValidInstanceId(existing)) {
    el.setAttribute('data-instance-id', existing);
    return existing!;
  }

  el.setAttribute('data-instance-id', instanceId);
  return instanceId;
};
